import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import { EligibilityYesAndNo } from "@/blocks/EligibilityYesAndNo";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

export const EligibilityParentGuardianStudentScreen = () => {
  const router = useRouter();

  const { supporterFirstName, supporterHouseholdMemberId } =
    useLocalSearchParams<{
      supporterFirstName: string;
      supporterHouseholdMemberId: string;
    }>();

  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);

  const handleYesPress = () => {
    setIsAddingNewHouseholdMember(true);
  };

  if (!currentHouseholdMember?.householdId) {
    return <VnLoadingSpinner className="mt-2" />;
  }

  const goToNextStep = () => {
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "PARENT_GUARDIAN_STUDENT",
        },
      },
      {
        onSuccess: () => {
          router.navigate({
            pathname: "/eligibility/support-disabled-individual",
            params: {
              supporterFirstName,
              supporterHouseholdMemberId,
            },
          });
        },
      },
    );
  };

  if (isAddingNewHouseholdMember) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={[
          "CHILD",
          "STEPCHILD",
          "GRANDCHILD",
          "FOSTER_CHILD",
          "OTHER",
        ]}
        ageRangeOptions={["YOUTH", "YOUNG_ADULT", "ADULT"]}
        assumptions={{
          taxFilingStatus: "NON_FILER",
          maritalStatus: "NEVER_MARRIED",
          childCaregiverStatus: "NOT_CAREGIVER",
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: supporterHouseholdMemberId
              ? "DEPENDENT_OF_SUPPORTER"
              : "SUPPORT_OF",
          },
          sameMailingAddress: true,
        }}
        titleI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityParentGuardianStudentScreen.supporterFormTitle"
            : "native:eligibilityParentGuardianStudentScreen.formTitle"
        }
        descriptionI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityParentGuardianStudentScreen.supporterFormDescription"
            : "native:eligibilityParentGuardianStudentScreen.formDescription"
        }
        titleI18nOptions={{
          supporterFirstName,
        }}
        descriptionI18nOptions={{
          supporterFirstName,
        }}
        onSuccessOrContinue={goToNextStep}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  return (
    <EligibilityYesAndNo
      titleI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilityParentGuardianStudentScreen.supporterTitle"
          : "native:eligibilityParentGuardianStudentScreen.title"
      }
      titleI18nOptions={{
        supporterFirstName,
      }}
      descriptionI18nOptions={{
        supporterFirstName,
      }}
      descriptionI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilityParentGuardianStudentScreen.supporterDescription"
          : "native:eligibilityParentGuardianStudentScreen.description"
      }
      onNoPress={goToNextStep}
      isNoLoading={isUpdatingHousehold}
      onYesPress={handleYesPress}
    />
  );
};
